/**
 * Awaited browser-to-mirror journal bridge for workspaceInfo (the project
 * metadata table). The service-core package cannot depend on the application
 * storage runtime, so the mounted mirror coordinator owns the root binding.
 *
 * A reservation is intentionally NOT cancelled after a failed database write:
 * a crash or a lost database acknowledgement leaves an ambiguous operation,
 * and remote publication must stop until local state has been verified.
 */
export type WorkspaceInfoSyncMutation = {
  wsName: string;
  projectSlug: string;
  workspaceType?: string;
  type: 'create' | 'update' | 'delete';
  expectedHash?: string;
  size?: number;
  previousHash?: string;
};

/** A private exact-byte fingerprint; no user text is put into the journal. */
export async function fingerprintWorkspaceInfo(value: unknown): Promise<{ expectedHash: string; size: number }> {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  const hash = await crypto.subtle.digest('SHA-256', bytes);
  return { expectedHash: [...new Uint8Array(hash)].map((byte) => byte.toString(16).padStart(2, '0')).join(''), size: bytes.byteLength };
}

type PreparingEvent = { mutation: WorkspaceInfoSyncMutation; pending: Array<Promise<string | undefined>> };
type CommittingEvent = { mutation: WorkspaceInfoSyncMutation; reservation: string; pending: Array<Promise<void>> };

export async function prepareWorkspaceInfoSyncMutation(mutation: WorkspaceInfoSyncMutation): Promise<string | undefined> {
  if (typeof window === 'undefined') return undefined;
  const detail: PreparingEvent = { mutation, pending: [] };
  window.dispatchEvent(new CustomEvent('elasticnote:workspace-info-preparing', { detail }));
  if (detail.pending.length > 1) throw new Error('Multiple workspace metadata journal authorities are active.');
  const [reservation] = await Promise.all(detail.pending);
  return reservation;
}

export async function commitWorkspaceInfoSyncMutation(mutation: WorkspaceInfoSyncMutation, reservation?: string): Promise<void> {
  if (!reservation || typeof window === 'undefined') return;
  const detail: CommittingEvent = { mutation, reservation, pending: [] };
  window.dispatchEvent(new CustomEvent('elasticnote:workspace-info-committing', { detail }));
  if (detail.pending.length !== 1) throw new Error('Workspace metadata changed locally, but no unique durable journal committer is available.');
  await Promise.all(detail.pending);
}
